import { RISK_COLORS } from '@client/design-system/__generated__';

// Shared special characters set - escaped for use in RegExp constructor
const SPECIAL_CHARS_FOR_REGEX = '!@#$%^&*()_+\\-=\\[\\]{}|;:\'",.<>?/\\\\`';

// Special characters set for direct regex literal usage
const SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:'",.<>?/\\`]/;

/**
 * Validates a password against the following requirements:
 * - At least 8 characters
 * - One uppercase letter (A-Z)
 * - One lowercase letter (a-z)
 * - One number (0-9)
 * - One special character (!@#$%^&*()_+-=[]{}|;:'",.<>?/\`)
 */
export function validatePassword(password: string) {
    const passwordRegex = new RegExp(
        `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[${SPECIAL_CHARS_FOR_REGEX}])[A-Za-z\\d${SPECIAL_CHARS_FOR_REGEX}]{8,}$`,
    );
    return passwordRegex.test(password);
}

/**
 * Individual requirement validators for granular feedback
 */
export const passwordRequirements = {
    minLength: (password: string) => password.length >= 8,
    hasUppercase: (password: string) => /[A-Z]/.test(password),
    hasLowercase: (password: string) => /[a-z]/.test(password),
    hasNumber: (password: string) => /[0-9]/.test(password),
    hasSpecialChar: (password: string) => SPECIAL_CHARS_REGEX.test(password),
};

/**
 * Gets the first failing password requirement error message
 * Returns null if password meets all requirements or is empty
 */
export function getPasswordValidationError(password: string): string | null {
    if (!password) return null;
    if (!passwordRequirements.minLength(password)) return 'Must be at least 8 characters';
    if (!passwordRequirements.hasUppercase(password)) return 'Must include an uppercase letter';
    if (!passwordRequirements.hasLowercase(password)) return 'Must include a lowercase letter';
    if (!passwordRequirements.hasNumber(password)) return 'Must include a number';
    if (!passwordRequirements.hasSpecialChar(password)) return 'Must include a special character';
    return null;
}

/**
 * Calculates password strength based on various factors
 * @returns score (0-100), level, color, and suggestions
 */
export function calculatePasswordStrength(password: string) {
    if (!password) {
        return {
            score: 0,
            level: 'Very Weak',
            color: RISK_COLORS.high,
            suggestions: ['Start typing a password'],
        };
    }

    let score = 0;
    const suggestions: string[] = [];

    // Length scoring (max 40 points)
    if (password.length >= 8) score += 10;
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;
    if (password.length >= 20) score += 10;

    if (password.length < 12) {
        suggestions.push('Use at least 12 characters for better security');
    }

    // Character variety scoring (max 40 points)
    if (passwordRequirements.hasLowercase(password)) score += 10;
    if (passwordRequirements.hasUppercase(password)) score += 10;
    if (passwordRequirements.hasNumber(password)) score += 10;
    if (passwordRequirements.hasSpecialChar(password)) score += 10;

    // Bonus for character diversity (max 20 points)
    const uniqueChars = new Set(password).size;
    const diversityRatio = uniqueChars / password.length;
    score += Math.floor(diversityRatio * 20);

    // Check for common patterns (deduct points)
    const commonPatterns = [
        /(.)\1{2,}/, // Repeated characters (aaa, 111)
        /^(123|abc|qwerty|password)/i, // Common sequences
        /(123|abc|qwe|asd)/i, // Common substrings
    ];

    for (const pattern of commonPatterns) {
        if (pattern.test(password)) {
            score = Math.max(0, score - 10);
            suggestions.push('Avoid common patterns and repeated characters');
            break;
        }
    }

    // Add specific suggestions based on missing requirements
    if (!passwordRequirements.hasUppercase(password)) {
        suggestions.push('Add uppercase letters');
    }
    if (!passwordRequirements.hasLowercase(password)) {
        suggestions.push('Add lowercase letters');
    }
    if (!passwordRequirements.hasNumber(password)) {
        suggestions.push('Add numbers');
    }
    if (!passwordRequirements.hasSpecialChar(password)) {
        suggestions.push('Add special characters');
    }

    // Ensure minimum score if all basic requirements are met
    const meetsBasicRequirements =
        passwordRequirements.minLength(password) &&
        passwordRequirements.hasUppercase(password) &&
        passwordRequirements.hasLowercase(password) &&
        passwordRequirements.hasNumber(password) &&
        passwordRequirements.hasSpecialChar(password);

    if (meetsBasicRequirements && score < 40) {
        score = 40;
    }

    // Determine strength level and color based on final score
    let level: string;
    let color: string;

    if (score < 20) {
        level = 'Very Weak';
        color = RISK_COLORS.high;
    } else if (score < 40) {
        level = 'Weak';
        color = RISK_COLORS.mediumHigh;
    } else if (score < 60) {
        level = 'Fair';
        color = RISK_COLORS.medium;
    } else if (score < 75) {
        level = 'Good';
        color = RISK_COLORS.mediumLow;
    } else if (score < 90) {
        level = 'Strong';
        color = RISK_COLORS.low;
    } else {
        level = 'Very Strong';
        color = RISK_COLORS.low;
    }

    return {
        score: Math.min(100, score),
        level,
        color,
        suggestions: suggestions.length > 0 ? suggestions : ['Great password!'],
    };
}
